import { z } from 'zod';

import type * as api from '@gitbook/api-client';

import type { AnalyticsChartSerieReport } from '../SiteAnalytics';

/** Language of the fenced block GitBook Agent draws a site analytics chart with. */
export const AGENT_CHART_SYNTAX = 'chart';

/** Inline code GitBook Agent ties a figure to a chart's line with, as in `chart:visitorsCount`. */
const AGENT_CHART_SWATCH = /^chart:([A-Za-z]+)$/;

const agentChartSchema = z.object({
    siteId: z.string(),
    title: z.string(),
    // The API checks the rest, and the chart shows what it rejected.
    query: z.custom<api.SiteInsightsQueryEventsAggregation>(
        (value) => typeof value === 'object' && value !== null && 'range' in value
    ),
});

export type AgentChart = z.infer<typeof agentChartSchema>;

/** The chart a ```chart block describes, or null while it streams in or when it is not one. */
export function parseAgentChart(code: string): AgentChart | null {
    try {
        const result = agentChartSchema.safeParse(JSON.parse(code));
        return result.success ? result.data : null;
    } catch {
        return null;
    }
}

/** The column an inline code ties its text to, or null when it is no swatch. */
export function parseAgentChartSwatch(code: string): string | null {
    return AGENT_CHART_SWATCH.exec(code)?.[1] ?? null;
}

/** The colour a column is drawn in across a message's charts, or null whenever it could mislead:
 * drawn in several colours or none, shared within its chart, or `text` not naming it as the legend. */
export function resolveAgentChartSwatch(input: {
    charts: AnalyticsChartSerieReport[][];
    column: string;
    text: string;
}): string | null {
    const { charts, column } = input;
    const lines = charts.flatMap((series) => series.filter((serie) => serie.column === column));
    const colors = new Set(lines.map((line) => line.color));
    const [color] = colors;
    if (colors.size !== 1 || !color) {
        return null;
    }

    // Past the end of the palette, colours come round again.
    const shared = charts.some(
        (series) =>
            series.some((serie) => serie.column === column) &&
            series.filter((serie) => serie.color === color).length > 1
    );
    if (shared) {
        return null;
    }

    // The swatch's own code names the column too, and must not vouch for itself.
    const text = input.text.replace(/chart:[A-Za-z]+/g, '').toLowerCase();
    return lines.every((line) => text.includes(line.label.toLowerCase())) ? color : null;
}
